import { Draggable, Droppable } from '@hello-pangea/dnd'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { isValidElement, PureComponent } from 'react'

import childrenSchema from './schemas/children'
import reactComponentSignatureSchema from './schemas/reactComponentSignature'
import combineEvents from './utils/combineEvents'

const renderItem = (provided, child) => (
  <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
    {child}
  </div>
)
const setRefs = (provided, childProps) => (ref) => {
  provided.innerRef(ref)
  if (childProps.setRef) {
    childProps.setRef(ref)
  }
}

class DraggableList extends PureComponent {
  renderCustomItem(provided, { isDragging }, child) {
    const { as, draggingClassName, setRef, ...childProps } = child.draggableProps || {}
    const withCustomHandler = combineEvents(childProps, provided.dragHandleProps)
    const ItemAs = as || 'div'
    const refPropertyName = typeof ItemAs === 'string' ? 'ref' : 'innerRef'
    return (
      <ItemAs
        {...childProps}
        {...{ [refPropertyName]: setRefs(provided, { setRef }) }}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        aria-label={childProps['aria-label']}
        aria-roledescription={childProps['aria-roledescription']}
        className={classnames(childProps.className, isDragging && draggingClassName)}
        onBlur={withCustomHandler('onBlur')}
        onFocus={withCustomHandler('onFocus')}
        onKeyDown={withCustomHandler('onKeyDown')}
        tabIndex={this.props.disableKeyboardDragging ? -1 : 0}
      >
        {child.element}
      </ItemAs>
    )
  }

  renderItems() {
    const { children, disabled } = this.props
    return children.map((child, index) => (
      <Draggable
        key={child.key}
        draggableId={child.key}
        index={index}
        isDragDisabled={disabled || child.isDragDisabled}
      >
        {(provided, snapshot) =>
          isValidElement(child) ? renderItem(provided, child) : this.renderCustomItem(provided, snapshot, child)
        }
      </Draggable>
    ))
  }

  render() {
    const { as, disabled, droppableId, droppableProps, isDropDisabled } = this.props

    const ListAs = as || 'div'
    // The ref from a Component is not the same as the ref from a ReactElement
    // https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/using-inner-ref.md
    const refPropertyName = typeof ListAs === 'string' ? 'ref' : 'innerRef'

    return (
      <Droppable droppableId={droppableId} isDropDisabled={isDropDisabled || disabled}>
        {
          /* (provided, snapshot) => ( */ (provided) => (
            // https://github.com/atlassian/react-beautiful-dnd#children-function
            <ListAs {...droppableProps} {...provided.droppableProps} {...{ [refPropertyName]: provided.innerRef }}>
              {this.renderItems()}
              {provided.placeholder}
            </ListAs>
          )
        }
      </Droppable>
    )
  }
}

DraggableList.propTypes = {
  children: PropTypes.arrayOf(childrenSchema).isRequired,

  as: reactComponentSignatureSchema,
  disabled: PropTypes.bool,
  disableKeyboardDragging: PropTypes.bool,
  droppableId: PropTypes.string,
  droppableProps: PropTypes.object,
  isDropDisabled: PropTypes.bool,
}

DraggableList.defaultProps = {
  as: null,
  disabled: false,
  disableKeyboardDragging: false,
  droppableId: 'droppable',
  droppableProps: {},
  isDropDisabled: false,
}

export default DraggableList
